import { useTranslation } from 'react-i18next';
import { Button } from '@folio/components/ui/button';
import type { DocumentUploadAttempt } from './useDocumentUploadQueue';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

export function DocumentUploadQueue({
  attempts,
  receivedDocumentIds,
  onRetry,
  onRemove,
  isUploading,
}: {
  attempts: DocumentUploadAttempt[];
  receivedDocumentIds: Set<string>;
  onRetry: (id?: string) => Promise<void>;
  onRemove: (id: string) => void;
  isUploading: boolean;
}) {
  const { t } = useTranslation();
  const visibleAttempts = attempts.filter(
    attempt => attempt.status !== 'uploaded' || !receivedDocumentIds.has(attempt.documentId),
  );
  const failedCount = attempts.filter(({ status }) => status === 'failed').length;
  const uploadedCount = attempts.filter(({ status }) => status === 'uploaded').length;
  if (visibleAttempts.length === 0) return null;

  return (
    <Stack asChild gap={2}>
      <section aria-label={t('documentReview.uploadResults')}>
        <Group wrap justify="between" gap={2}>
          <Text size="sm" role="status">
            {t('documentReview.uploaded', { count: uploadedCount })}
            {' · '}
            {t('documentReview.failed', { count: failedCount })}
          </Text>
          {failedCount > 1 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isUploading}
              onClick={() => onRetry()}
            >
              {t('documentReview.retryFailed')}
            </Button>
          )}
        </Group>
        <ul className="divide-y rounded-lg border">
          {visibleAttempts.map(attempt => (
            <li key={attempt.id} className="flex flex-wrap items-center gap-3 p-3">
              <div className="min-w-0 flex-1">
                <Text size="sm" weight="medium" className="break-words">
                  {attempt.file.name}
                </Text>
                <Text size="sm" tone={attempt.status === 'failed' ? 'destructive' : 'muted'}>
                  {t(
                    attempt.status === 'failed'
                      ? 'documentReview.uploadFailed'
                      : attempt.status === 'uploading'
                        ? 'documentReview.uploading'
                        : 'documentReview.uploadComplete',
                  )}
                </Text>
              </div>
              {attempt.status === 'failed' && (
                <Group align="stretch" gap={2}>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={isUploading}
                    aria-label={t('documentReview.retryFile', { name: attempt.file.name })}
                    onClick={() => onRetry(attempt.id)}
                  >
                    {t('documentReview.retry')}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label={t('documentReview.removeFailed', { name: attempt.file.name })}
                    onClick={() => onRemove(attempt.id)}
                  >
                    {t('documentReview.remove')}
                  </Button>
                </Group>
              )}
            </li>
          ))}
        </ul>
      </section>
    </Stack>
  );
}
